import type { Ticket, TicketState } from './types'

/**
 * What reception may do with a ticket it found by number.
 *
 * "Find a ticket" used to offer removal only for the last eight tickets this
 * page had issued this session — everything else about a wrong kiosk ticket had
 * to sit until it aged into a no-show and skewed the no-show rate. The server
 * never drew that line. api/tickets.go's "void" case (~375-382) lets an
 * operator void the ticket waiting in front of them, and a supervisor void
 * anything the engine will still take: engine/flow.go's Void (~965-980) asks
 * queue.Apply(state, ActionVoid), and that transition table (queue/state.go)
 * only answers yes from waiting, called, parked or no_show — a ticket once
 * being served is part of the day's record, and voiding it would erase that
 * record, whoever is asking. This file mirrors both rules rather than
 * restating a third copy that could drift from either — the next change to the
 * API guard or the transition table should find this comment and this file.
 *
 * Putting a no-show back in line is a different, older feature (recycleTicket
 * in Reception.tsx) and does not belong to this rule at all: the engine's
 * Recycle only checks CanOperateCounter, the ordinary reception permission, so
 * every desk gets it, supervisor or not, once a no-show is standing in front of
 * it as a found ticket.
 *
 * A found ticket may also be moved, to another service's line or to another
 * place in its own, while it is still only waiting. queue/state.go's
 * ActionMove is legal from StateWaiting alone, and engine/flow.go's Move
 * checks the same permission Void does above: whoever may issue may act on a
 * ticket that has not yet been called. This file mirrors that too, so the
 * next change to either finds this comment.
 */

/** The states Void can leave, per queue/state.go's transition table. Anything
 *  else — serving, wrapup, closed, void itself — has no way through it, whoever
 *  is asking, so no button this screen could draw would ever succeed. */
const VOIDABLE: readonly TicketState[] = ['waiting', 'called', 'parked', 'no_show']

export function findActions(
  ticket: Pick<Ticket, 'state'>,
  canSupervise: boolean,
): Array<'putBack' | 'remove' | 'move'> {
  const actions: Array<'putBack' | 'remove' | 'move'> = []
  if (ticket.state === 'no_show') actions.push('putBack')
  if (VOIDABLE.includes(ticket.state) && (ticket.state === 'waiting' || canSupervise)) {
    actions.push('remove')
  }
  if (ticket.state === 'waiting') actions.push('move')
  return actions
}
